import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getCommentToken } from '../utils/commentTokens';
import { addVideoFrameHints } from '../utils/videoFrameHint';
import { applyEmbedFacades } from '../utils/embedFacades';
//import { formatDocumentNumber } from '../utils/documentNumber';
import { convertPlainTextToHtml, isRichTextEmpty, normalizeRichTextContent } from '../utils/richText';
import { getConfidenceLevel } from '../utils/confidence';
import { resolveMediaUrl, resolveRichTextMedia } from '../utils/media';
import {
    getPostCustomThumbnail,
    getPostVideo,
    getVideoProviderLabel,
    getVideoSourceUrl,
} from '../utils/video';
import { getLaymenAuthorLabel } from '../utils/laymenAlias';
import PostSuggestions from './PostSuggestions';
import VideoEmbed from './VideoEmbed';
import CreatorLink from './CreatorLink';

const COMMENT_TOKEN_STORAGE_KEY = 'comment_edit_tokens';

const CommentThread = ({ comment, depth = 0, onReply, onEdit, onDelete }) => {
    const commentId = comment._id || comment.id;

    const [showReply, setShowReply] = useState(false);
    const [replyAlias, setReplyAlias] = useState('');
    const [replyPassword, setReplyPassword] = useState('');
    const [replyContent, setReplyContent] = useState('');
    const [replyError, setReplyError] = useState(null);
    const [replyLoading, setReplyLoading] = useState(false);

    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState(comment.content || comment.text || '');
    const [editError, setEditError] = useState(null);
    const [editLoading, setEditLoading] = useState(false);

    const [showDelete, setShowDelete] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [storedToken, setStoredToken] = useState(() => getCommentToken(commentId));

    useEffect(() => {
        setEditContent(comment.content || comment.text || '');
    }, [comment.content, comment.text]);

    useEffect(() => {
        const updateToken = () => {
            setStoredToken(getCommentToken(commentId));
        };

        updateToken();

        if (typeof window === 'undefined') {
            return undefined;
        }

        const current = getCommentToken(commentId);
        if (current?.expiresAt) {
            const expires = new Date(current.expiresAt).getTime();
            const delay = expires - Date.now() + 500;
            if (delay > 0) {
                const timeoutId = window.setTimeout(updateToken, delay);
                return () => window.clearTimeout(timeoutId);
            }
        }

        return undefined;
    }, [commentId, comment.updatedAt]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }
        const handleStorage = (event) => {
            if (event && event.key && event.key !== COMMENT_TOKEN_STORAGE_KEY) {
                return;
            }
            setStoredToken(getCommentToken(commentId));
        };
        window.addEventListener('storage', handleStorage);
        return () => {
            window.removeEventListener('storage', handleStorage);
        };
    }, [commentId]);

    useEffect(() => {
        if (!storedToken) {
            setIsEditing(false);
            setShowDelete(false);
        }
    }, [storedToken]);

    const modificationAllowed = Boolean(storedToken?.token);
    const tokenExpiryLabel = storedToken?.expiresAt
        ? new Date(storedToken.expiresAt).toLocaleTimeString()
        : null;

    const handleReplySubmit = async (event) => {
        event.preventDefault();

        if (!replyContent.trim()) {
            setReplyError('FIELD NOTE REQUIRED');
            return;
        }

        setReplyError(null);

        if (!onReply) {
            return;
        }

        setReplyLoading(true);
        try {
            await onReply({
                alias: replyAlias.trim(),
                password: replyPassword.trim(),
                content: replyContent.trim(),
                parentId: commentId,
            });
            setReplyAlias('');
            setReplyPassword('');
            setReplyContent('');
            setShowReply(false);
        } catch (error) {
            setReplyError(error.message || 'UNABLE TO LOG FIELD NOTE');
        } finally {
            setReplyLoading(false);
        }
    };

    const handleEditSubmit = async (event) => {
        event.preventDefault();

        if (!editContent.trim()) {
            setEditError('FIELD NOTE REQUIRED');
            return;
        }

        if (!modificationAllowed) {
            setEditError('EDIT WINDOW EXPIRED');
            return;
        }

        setEditError(null);
        setEditLoading(true);

        try {
            await onEdit?.({
                commentId,
                editToken: storedToken.token,
                content: editContent.trim(),
            });
            setIsEditing(false);
            const refreshed = getCommentToken(commentId);
            setStoredToken(refreshed);
        } catch (error) {
            setEditError(error.message || 'UNABLE TO UPDATE NOTE');
        } finally {
            setEditLoading(false);
        }
    };

    const handleDeleteSubmit = async (event) => {
        event.preventDefault();

        if (!modificationAllowed) {
            setDeleteError('EDIT WINDOW EXPIRED');
            return;
        }

        setDeleteError(null);
        setDeleteLoading(true);

        try {
            await onDelete?.({
                commentId,
                editToken: storedToken.token,
            });
        } catch (error) {
            setDeleteError(error.message || 'UNABLE TO DELETE NOTE');
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <li className={`comment-item comment-depth-${depth}`}>
            <div className="comment-meta">
                <span className="comment-author">
                    {comment.alias}
                    {comment.tripcode && (
                        <span className="comment-tripcode">{comment.tripcode}</span>
                    )}
                </span>
                <span className="comment-timestamp">
                    {new Date(comment.createdAt || comment.recordedAt).toLocaleString()}
                </span>
            </div>
            <p className="comment-text">{comment.content || comment.text}</p>
            <div className="comment-actions">
                <button
                    type="button"
                    className="reply-button"
                    onClick={() => {
                        setShowReply(previous => !previous);
                        setReplyError(null);
                    }}
                >
                    {showReply ? 'CANCEL' : 'REPLY'}
                </button>
                {modificationAllowed && (
                    <>
                        <button
                            type="button"
                            className="reply-button"
                            onClick={() => {
                                setIsEditing(previous => !previous);
                                setEditError(null);
                                setEditContent(comment.content || comment.text || '');
                            }}
                        >
                            {isEditing ? 'CANCEL EDIT' : 'EDIT'}
                        </button>
                        <button
                            type="button"
                            className="reply-button"
                            onClick={() => {
                                setShowDelete(previous => !previous);
                                setDeleteError(null);
                            }}
                        >
                            {showDelete ? 'CANCEL DELETE' : 'DELETE'}
                        </button>
                    </>
                )}
            </div>
            {modificationAllowed && tokenExpiryLabel && (
                <p className="token-hint">EDIT ACCESS UNTIL {tokenExpiryLabel}</p>
            )}
            {showReply && (
                <form className="reply-form" onSubmit={handleReplySubmit}>
                    <div className="form-grid">
                        <label className="form-field">
                            <span>Anynomous Name (Optional)</span>
                            <input
                                type="text"
                                value={replyAlias}
                                onChange={(event) => setReplyAlias(event.target.value)}
                                placeholder="Enter Name"
                            />
                        </label>
                        <label className="form-field">
                            <span>TRIPCODE KEY (OPTIONAL)</span>
                            <input
                                type="password"
                                value={replyPassword}
                                onChange={(event) => setReplyPassword(event.target.value)}
                                placeholder="ENTER SECRET KEY"
                            />
                            <span className="form-hint">
                                Same key recreates your tripcode for future notes.
                            </span>
                        </label>
                        <label className="form-field form-field-wide">
                            <span>FIELD NOTE</span>
                            <textarea
                                value={replyContent}
                                onChange={(event) => setReplyContent(event.target.value)}
                                placeholder="ENTER FINDINGS OR OBSERVATIONS..."
                                rows={3}
                            />
                        </label>
                    </div>
                    {replyError && <p className="form-error">{replyError}</p>}
                    <button
                        type="submit"
                        className="submit-comment"
                        disabled={replyLoading}
                    >
                        {replyLoading ? 'LOGGING NOTE...' : 'REPLY WITH NOTE'}
                    </button>
                </form>
            )}

            {isEditing && modificationAllowed && (
                <form className="reply-form" onSubmit={handleEditSubmit}>
                    <div className="form-grid">
                        <label className="form-field form-field-wide">
                            <span>UPDATE NOTE</span>
                            <textarea
                                value={editContent}
                                onChange={(event) => setEditContent(event.target.value)}
                                rows={4}
                            />
                        </label>
                    </div>
                    {editError && <p className="form-error">{editError}</p>}
                    <button
                        type="submit"
                        className="submit-comment"
                        disabled={editLoading}
                    >
                        {editLoading ? 'UPDATING NOTE...' : 'CONFIRM EDIT'}
                    </button>
                </form>
            )}

            {showDelete && modificationAllowed && (
                <form className="reply-form delete-form" onSubmit={handleDeleteSubmit}>
                    <p className="delete-warning">
                        THIS ACTION IS IRREVERSIBLE.
                    </p>
                    {deleteError && <p className="form-error">{deleteError}</p>}
                    <button
                        type="submit"
                        className="submit-comment delete-button"
                        disabled={deleteLoading}
                    >
                        {deleteLoading ? 'REMOVING NOTE...' : 'DELETE NOTE'}
                    </button>
                </form>
            )}

            {comment.replies && comment.replies.length > 0 && (
                <ul className="comment-children">
                    {comment.replies.map((reply) => (
                        <CommentThread
                            key={reply._id || reply.id}
                            comment={reply}
                            depth={Math.min(depth + 1, 5)}
                            onReply={onReply}
                            onEdit={onEdit}
                            onDelete={onDelete}
                        />
                    ))}
                </ul>
            )}
        </li>
    );
};

const PostDetail = ({
    post,
    comments = [],
    onBack,
    onAddComment,
    onEditComment,
    onDeleteComment,
    onSelectPostId,
}) => {
    const [commenter, setCommenter] = useState('');
    const [commentPassword, setCommentPassword] = useState('');
    const [commentText, setCommentText] = useState('');
    const [formError, setFormError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const richTextRef = useRef(null);
    const orderedComments = useMemo(() => (
        Array.isArray(comments) ? comments : []
    ), [comments]);

    // Body and bibliography embeds become click-to-play facades before the
    // browser paints (re-run whenever the post, and so the HTML, changes).
    useLayoutEffect(() => {
        if (post) {
            applyEmbedFacades(richTextRef.current);
        }
    }, [post]);

    // Every hook must run before this early return, or a post -> null
    // re-render calls fewer hooks than the last one and React throws.
    if (!post) {
        return null;
    }

    const confidenceValue = Number(post?.confidenceRating ?? post?.confidenceScoreRating);
    const { label: confidenceLabel, tone: confidenceTone } = getConfidenceLevel(confidenceValue);
    const video = getPostVideo(post);
    // Condoned Media: the player replaces the thumbnail in this reader too.
    const thumbnailSrc = video ? '' : resolveMediaUrl(post?.thumbnail);
    const videoSourceUrl = getVideoSourceUrl(video);
    const authorLabel = getLaymenAuthorLabel(post);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!commentText.trim()) {
            setFormError('FIELD NOTE REQUIRED');
            return;
        }

        setFormError(null);
        setIsSubmitting(true);

        try {
            await onAddComment?.({
                alias: commenter.trim(),
                password: commentPassword.trim(),
                content: commentText.trim(),
                parentId: null,
            });

            setCommenter('');
            setCommentPassword('');
            setCommentText('');
        } catch (error) {
            setFormError(error.message || 'UNABLE TO LOG FIELD NOTE');
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderContent = () => {
        // Empty content falls back to the description, which is plain text
        // (and not sanitized server-side): always escape it, even when it
        // looks like HTML.
        const htmlContent = typeof post.content === 'string' && post.content.trim().length > 0
            ? addVideoFrameHints(resolveRichTextMedia(normalizeRichTextContent(post.content)))
            : convertPlainTextToHtml(post.description);

        return (
            <div
                className="post-detail-rich-text"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
        );
    };

    const renderBibliography = () => {
        const bibliographySource = typeof post.bibliography === 'string' ? post.bibliography : '';

        if (!bibliographySource.trim()) {
            return null;
        }

        const htmlContent = addVideoFrameHints(resolveRichTextMedia(normalizeRichTextContent(bibliographySource)));

        if (isRichTextEmpty(htmlContent)) {
            return null;
        }

        return (
            <section className="post-detail-bibliography">
                <h3 className="post-detail-bibliography__title">Bibliography</h3>
                <div
                    className="post-detail-rich-text post-detail-bibliography__content"
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                />
            </section>
        );
    };

    return (
        <section className="post-detail">
            <div className="post-detail-panel">
                <button type="button" className="back-button" onClick={onBack}>
                    Return to Home
                </button>
                {video && (
                    <div className="post-detail-video">
                        <VideoEmbed
                            video={video}
                            title={post.title}
                            poster={getPostCustomThumbnail(post)}
                        />
                        {videoSourceUrl && (
                            <CreatorLink className="post-detail-video__source" href={videoSourceUrl}>
                                {video.channel ? `Video by ${video.channel} · ` : ''}Watch on {getVideoProviderLabel(video)} ↗
                            </CreatorLink>
                        )}
                    </div>
                )}
                {thumbnailSrc && (
                    <div className="post-detail-thumbnail">
                        <img src={thumbnailSrc} alt={`${post.title} thumbnail`} />
                    </div>
                )}
                <header className="post-detail-header">
                    {/* <span className="copy-number">{formatDocumentNumber(post.documentNumber ?? post.copyNumber)}</span> */}
                    <h2 className="post-detail-title">{post.title}</h2>
                    <div className="post-meta">
                        <span>AUTHOR: {authorLabel}</span>
                        <span>DATE: {post.date}</span>
                        <span className="post-meta__confidence">
                            CONFIDENCE LEVEL:{' '}
                            <span className={`confidence-chip confidence-chip--${confidenceTone}`}>
                                {confidenceLabel}
                            </span>
                        </span>
                        <span>
                            SUBJECT(S): {post.tags?.map((tag) => (
                                // <span key={tag} className="post-tag">
                                //     {tag}
                                // </span>
                                `${tag}`
                                )).join(', ')}
                        </span>
                    </div>
                    {/* <div className="post-tags">
                        Tags: {post.tags?.map((tag) => (
                                // <span key={tag} className="post-tag">
                                //     {tag}
                                // </span>
                                `${tag}`
                                )).join(', ')}
                    </div> */}
                </header>

                <article className="post-detail-content" ref={richTextRef}>
                    {/* <div className="post-detail-banner">
                        <span className="banner-label">ACCESS LEVEL: CLEARED</span>
                        <span className="banner-note">
                            Full View
                        </span>
                    </div> */}
                    {renderContent()}
                    {renderBibliography()}
                </article>

                <section className="post-comments">
                    {/* <h3 className="comments-title">FIELD NOTES &amp; OBSERVATIONS</h3> */}
                    <h3 className="comments-title">Comments and Discussions</h3>
                    {orderedComments.length === 0 ? (
                        <p className="no-comments">No Comments</p>
                    ) : (
                        <ul className="comment-feed">
                            {orderedComments.map((comment) => (
                                <CommentThread
                                    key={comment._id || comment.id}
                                    comment={comment}
                                    depth={0}
                                    onReply={onAddComment}
                                    onEdit={onEditComment}
                                    onDelete={onDeleteComment}
                                />
                            ))}
                        </ul>
                    )}

                    <form className="comment-form" onSubmit={handleSubmit}>
                        <div className="form-grid">
                            <label className="form-field">
                                <span>Anynomous Name (optional)</span>
                                <input
                                    type="text"
                                    value={commenter}
                                    onChange={(event) => setCommenter(event.target.value)}
                                    placeholder="Enter Name"
                                />
                            </label>
                            <label className="form-field">
                                <span>TRIPCODE KEY (OPTIONAL)</span>
                                <input
                                    type="password"
                                    value={commentPassword}
                                    onChange={(event) => setCommentPassword(event.target.value)}
                                    placeholder="Enter Optional Password"
                                />
                                <span className="form-hint">
                                    Instead of creating an account; this allows your alias to have permanence
                                </span>
                            </label>
                            <label className="form-field form-field-wide">
                                <span>Write a Comment</span>
                                <textarea
                                    value={commentText}
                                    onChange={(event) => setCommentText(event.target.value)}
                                    placeholder="Enter Comment here"
                                    rows={4}
                                />
                            </label>
                        </div>
                        {formError && <p className="form-error">{formError}</p>}
                        <button
                            type="submit"
                            className="submit-comment"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Posting Comment...' : 'Post Comment'}
                        </button>
                    </form>
                </section>

                <PostSuggestions post={post} onSelectPostId={onSelectPostId} />
            </div>
        </section>
    );
};

export default PostDetail;
